/**
 * Notebook Store — persistent investigation case storage.
 * Pure localStorage CRUD. No UI.
 *
 * Storage layout:
 *   unveil:notebook          — case index (lightweight array)
 *   unveil:notebook:active   — active case ID
 *   unveil:notebook:case:{id} — entries for a single case
 */

import { safeGetItem, safeReadJSON, safeRemoveItem, safeSetItem } from './storage.js';

const INDEX_KEY = 'unveil:notebook';
const ACTIVE_KEY = 'unveil:notebook:active';
const CASE_PREFIX = 'unveil:notebook:case:';
const MAX_CASES = 20;
const MAX_ENTRIES = 100;

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ─── Case index ──────────────────────────────────────────────────────────────

function readJSON(key, fallback) {
  return safeReadJSON(key, fallback);
}

function writeJSON(key, data) {
  return safeSetItem(key, JSON.stringify(data), 'Notebook could not be saved. Local storage may be full.');
}

export function getCaseIndex() {
  return readJSON(INDEX_KEY, []);
}

function setCaseIndex(arr) {
  return writeJSON(INDEX_KEY, arr);
}

// ─── Active case ─────────────────────────────────────────────────────────────

export function getActiveCaseId() {
  return safeGetItem(ACTIVE_KEY, '');
}

export function setActiveCaseId(id) {
  return safeSetItem(ACTIVE_KEY, id, 'Active notebook case could not be updated.');
}

// ─── Case CRUD ───────────────────────────────────────────────────────────────

export function createCase(name) {
  const index = getCaseIndex();
  if (index.length >= MAX_CASES) return null;

  const c = {
    id: uid(),
    name: name || 'Untitled Case',
    created: Date.now(),
    updated: Date.now(),
    entryCount: 0,
  };

  index.unshift(c);
  if (!setCaseIndex(index)) return null;
  if (!writeJSON(CASE_PREFIX + c.id, [])) return null;
  setActiveCaseId(c.id);
  return c;
}

export function renameCase(caseId, newName) {
  const index = getCaseIndex();
  const c = index.find((x) => x.id === caseId);
  if (!c) return;
  c.name = newName;
  c.updated = Date.now();
  return setCaseIndex(index);
}

export function deleteCase(caseId) {
  setCaseIndex(getCaseIndex().filter((x) => x.id !== caseId));
  safeRemoveItem(CASE_PREFIX + caseId, 'Notebook case could not be deleted.');

  if (getActiveCaseId() === caseId) {
    const remaining = getCaseIndex();
    setActiveCaseId(remaining.length ? remaining[0].id : '');
  }
}

// ─── Entries ─────────────────────────────────────────────────────────────────

export function getEntries(caseId) {
  return readJSON(CASE_PREFIX + caseId, []);
}

function setEntries(caseId, entries) {
  if (!writeJSON(CASE_PREFIX + caseId, entries)) return false;

  // Update entryCount in index
  const index = getCaseIndex();
  const c = index.find((x) => x.id === caseId);
  if (c) {
    c.entryCount = entries.length;
    c.updated = Date.now();
    return setCaseIndex(index);
  }
  return true;
}

export function addEntry(caseId, entry) {
  const entries = getEntries(caseId);
  if (entries.length >= MAX_ENTRIES) return null;

  const full = { id: uid(), caseId, created: Date.now(), ...entry };
  entries.unshift(full);
  return setEntries(caseId, entries) ? full : null;
}

export function deleteEntry(caseId, entryId) {
  const entries = getEntries(caseId).filter((e) => e.id !== entryId);
  return setEntries(caseId, entries);
}

export function updateNote(caseId, entryId, text) {
  const entries = getEntries(caseId);
  const entry = entries.find((e) => e.id === entryId);
  if (entry && entry.type === 'note') {
    entry.text = text;
    return setEntries(caseId, entries);
  }
  return false;
}

// ─── Pin helper (called from main.js) ────────────────────────────────────────

export function pinToNotebook({ toolId, toolLabel, content, inputSummary }) {
  let caseId = getActiveCaseId();

  // Auto-create a case if none exists
  if (!caseId || !getCaseIndex().find((c) => c.id === caseId)) {
    const now = new Date();
    const name = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const c = createCase(name);
    if (!c) return null;
    caseId = c.id;
  }

  return addEntry(caseId, {
    type: 'pin',
    toolId,
    toolLabel,
    content,
    inputSummary: inputSummary ? inputSummary.slice(0, 80) : '',
  });
}

// ─── Export / Import ─────────────────────────────────────────────────────────

export function exportCase(caseId) {
  const index = getCaseIndex();
  const c = index.find((x) => x.id === caseId);
  if (!c) return null;
  return JSON.stringify({ case: c, entries: getEntries(caseId) }, null, 2);
}

export function importCase(jsonString) {
  let data;
  try { data = JSON.parse(jsonString); }
  catch { return null; }

  if (!data?.case?.name || !Array.isArray(data?.entries)) return null;

  const index = getCaseIndex();
  if (index.length >= MAX_CASES) return null;

  // Assign new IDs to avoid collisions
  const newCaseId = uid();
  const c = {
    id: newCaseId,
    name: data.case.name,
    created: data.case.created || Date.now(),
    updated: Date.now(),
    entryCount: data.entries.length,
  };

  const entries = data.entries.map((e) => ({
    ...e,
    id: uid(),
    caseId: newCaseId,
  }));

  index.unshift(c);
  if (!setCaseIndex(index)) return null;
  if (!writeJSON(CASE_PREFIX + newCaseId, entries)) return null;
  return c;
}
